@if (isPublicRoute) {
  <app-alert></app-alert>
  <router-outlet></router-outlet>
}
@else if (appAuthService.isRegionNotSupported$()) {
  <app-alert></app-alert>
  <div class="flex min-h-screen w-full flex-col items-center justify-center bg-[#050914] text-slate-50 px-4 py-12">
    <div class="w-full max-w-md rounded-2xl border border-white/5 bg-slate-900/60 p-6 shadow-2xl text-center backdrop-blur-md">
      <!-- Icon -->
      <div class="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-500/10 text-red-400">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-7 h-7">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
        </svg>
      </div>

      <h2 class="text-xl font-semibold mb-2 text-white">Profile Not Supported</h2>
      <p class="text-sm text-slate-400 mb-6">
        Your profile is not supported in <span class="font-semibold text-slate-200">{{ currentRegionDisplayName$() }}</span>.
      </p>

      @if (homeRegion$(); as home) {
        <p class="text-xs text-slate-500 mb-6">
          Please switch back to your home region to access your account.
        </p>
        <button 
          type="button" 
          (click)="switchRegionBack()" 
          class="w-full rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-500 active:scale-[0.98] shadow-lg shadow-indigo-600/25"
        >
          Switch to {{ home.displayName }}
        </button>
      } @else {
        <p class="text-xs text-slate-500 mb-6">
          Please switch to your home region to access your account.
        </p>
        <div class="flex flex-col gap-2 max-h-48 overflow-y-auto pr-1">
          @for (region of supportedRegions; track region.id) {
            <button 
              type="button" 
              (click)="switchToSpecificRegion(region.id)" 
              class="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/10 active:scale-[0.98] text-left flex justify-between items-center"
            >
              <span>{{ region.displayName }}</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4 text-slate-400">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </button>
          }
        </div>
      }
    </div>
  </div>
}
@else {
  @if (impersonation.state$().active) {
    <div class="flex items-center justify-between gap-3 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 px-4 py-2.5 text-xs font-semibold text-slate-950 shadow-xl sticky top-0 z-50">
      <div class="flex items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4 shrink-0">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9-.03a9 9 0 1 1 18 0 9 9 0 0 1-18 0Zm12-3.75h.008v.008H15V8.25Z" />
        </svg>
        <span>ACTIVE IMPERSONATION SESSION: Currently acting as <strong class="underline font-bold">{{ impersonation.state$().targetDisplayName || impersonation.state$().targetIdentityUserId }}</strong></span>
      </div>
      <button 
        type="button" 
        class="rounded-lg bg-slate-950 text-amber-300 px-3 py-1.5 text-xs font-bold shadow-md transition hover:bg-slate-900 active:scale-95 disabled:opacity-50"
        (click)="impersonation.stop()" 
        [disabled]="impersonation.loading$()">
        {{ impersonation.loading$() ? 'Stopping...' : 'Stop Impersonation' }}
      </button>
    </div>
  }
  @if (shouldShowOnboardingProgress) {
    <app-my-onboarding-progress [progress]="onboarding.status$()"></app-my-onboarding-progress>
  }
  <app-alert></app-alert>
  <router-outlet></router-outlet>
}

@if (regionService.isSwitchingRegion$()) {
  <div
    class="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-slate-950/85 px-4 backdrop-blur-md transition-all duration-300"
    role="dialog"
    aria-modal="true"
    aria-label="Switching region"
  >
    <div class="relative flex w-full max-w-sm flex-col items-center rounded-2xl border border-sky-500/25 bg-slate-900/95 p-6 text-center shadow-2xl shadow-sky-950/60">
      <!-- Glow ambient background -->
      <div class="pointer-events-none absolute -top-12 h-32 w-32 rounded-full bg-sky-500/15 blur-2xl"></div>

      <!-- Region Flag with Animated Pulse Ring -->
      <div class="relative mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-sky-400/30 bg-slate-950/90 shadow-[0_0_24px_rgba(56,189,248,0.25)]">
        <span class="text-3xl leading-none select-none">
          {{ regionService.getRegionFlag(regionService.switchingTargetRegion$()?.id) }}
        </span>
        <div class="absolute inset-0 rounded-full border border-sky-400/40 animate-ping opacity-20"></div>
      </div>

      <!-- Title & Region -->
      <h3 class="text-base font-semibold text-white tracking-tight">
        Switching to {{ regionService.switchingTargetRegion$()?.displayName || 'New Region' }}
      </h3>
      <p class="mt-1.5 text-xs text-slate-400 max-w-[260px] leading-relaxed">
        Refreshing clinical context, user profile, and regional records...
      </p>

      <!-- Smooth Progress Loader Bar -->
      <div class="mt-5 w-full overflow-hidden rounded-full bg-slate-800 h-1.5">
        <div class="h-full w-full rounded-full bg-gradient-to-r from-sky-400 via-indigo-400 to-sky-400 animate-pulse"></div>
      </div>
    </div>
  </div>
}
